import { useState, useEffect } from "react";
import axios from "axios";
import { Cloud, Download, Settings, Calendar, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BackupSettings = () => {
  const [config, setConfig] = useState({
    auto_backup_enabled: false,
    backup_interval_hours: 12,
    spreadsheet_id: null,
    last_backup_at: null
  });
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/backup/config`, { withCredentials: true });
      setConfig(response.data);
    } catch (error) {
      toast.error("Failed to load backup configuration");
    }
  };

  const handleConnectGoogleSheets = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/oauth/sheets/login`, { withCredentials: true });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect Google Sheets");
      setConnecting(false);
    }
  };

  const handleManualBackup = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/backup/manual`, {}, { withCredentials: true });
      toast.success("Backup completed successfully!");
      setConfig({ ...config, last_backup_at: new Date().toISOString(), spreadsheet_id: response.data.spreadsheet_id });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Backup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (updates) => {
    try {
      const response = await axios.put(`${API}/backup/config`, updates, { withCredentials: true });
      setConfig(response.data);
      toast.success("Settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-outfit font-semibold text-stone-900">
            Google Sheets Connection
          </CardTitle>
          <CardDescription>Connect your Google account to enable backups</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {config.spreadsheet_id ? (
            <div className="flex items-center justify-between p-4 bg-lime-50 border border-lime-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center">
                  <Cloud className="w-5 h-5 text-lime-700" />
                </div>
                <div>
                  <p className="font-medium text-stone-900">Connected to Google Sheets</p>
                  <p className="text-sm text-stone-600">Backup spreadsheet is ready</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => window.open(`https://docs.google.com/spreadsheets/d/${config.spreadsheet_id}`, '_blank')}
                data-testid="view-spreadsheet-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Sheet
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cloud className="w-8 h-8 text-stone-400" />
              </div>
              <p className="text-stone-600 mb-4">Connect Google Sheets to enable automated backups</p>
              <Button
                onClick={handleConnectGoogleSheets}
                disabled={connecting}
                className="bg-lime-700 text-white hover:bg-lime-800"
                data-testid="connect-sheets-btn"
              >
                {connecting ? "Connecting..." : "Connect Google Sheets"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Backup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-outfit font-semibold text-stone-900">
            Manual Backup
          </CardTitle>
          <CardDescription>Create an instant backup of your inventory data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {config.last_backup_at && (
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <Calendar className="w-4 h-4" />
                <span>Last backup: {new Date(config.last_backup_at).toLocaleString()}</span>
              </div>
            )}
            <Button
              onClick={handleManualBackup}
              disabled={loading || !config.spreadsheet_id}
              className="bg-lime-700 text-white hover:bg-lime-800"
              data-testid="manual-backup-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {loading ? "Creating Backup..." : "Backup Now"}
            </Button>
            {!config.spreadsheet_id && (
              <p className="text-sm text-amber-600">Connect Google Sheets first to enable backup</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Auto Backup Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-outfit font-semibold text-stone-900">
            Automatic Backup
          </CardTitle>
          <CardDescription>Schedule regular automated backups</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base font-medium">Enable Auto Backup</Label>
              <p className="text-sm text-stone-500">Automatically backup at scheduled intervals</p>
            </div>
            <Switch
              checked={config.auto_backup_enabled}
              onCheckedChange={(checked) => handleUpdateConfig({ auto_backup_enabled: checked })}
              disabled={!config.spreadsheet_id}
              data-testid="auto-backup-toggle"
            />
          </div>

          {config.auto_backup_enabled && (
            <div className="space-y-4 pt-4 border-t border-stone-200">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Backup Interval</Label>
                  <span className="text-sm font-semibold text-lime-700">
                    Every {config.backup_interval_hours} hour{config.backup_interval_hours !== 1 ? 's' : ''}
                  </span>
                </div>
                <Slider
                  value={[config.backup_interval_hours]}
                  onValueChange={(value) => setConfig({ ...config, backup_interval_hours: value[0] })}
                  onValueCommit={(value) => handleUpdateConfig({ backup_interval_hours: value[0] })}
                  min={1}
                  max={24}
                  step={1}
                  className="w-full"
                  data-testid="backup-interval-slider"
                />
                <div className="flex justify-between text-xs text-stone-500">
                  <span>1 hour</span>
                  <span>24 hours</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backup Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-outfit font-semibold text-stone-900">
            What Gets Backed Up?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-stone-600">
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-lime-700 rounded-full"></div>
              All product information and categories
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-lime-700 rounded-full"></div>
              Vendor details
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-lime-700 rounded-full"></div>
              Complete transaction history
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-lime-700 rounded-full"></div>
              Current stock levels (in kg)
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-lime-700 rounded-full"></div>
              Threshold values for low stock alerts
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupSettings;